import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { AnalysisFolder } from "@/zapi-frontend/shared/ui/AnalysisFolder/AnalysisFolder";
import { CancelAnalysisModal } from "@/zapi-frontend/shared/ui/CancelAnalysisModal";
import type { TSetupProgress } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { formatMinutesLeft, setupMinutesLeft } from "@/zapi-frontend/shared/lib/setupProgress";

const CLOCK_TICK_MS = 10 * 1000;

function useNow(tickMs: number): number {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), tickMs);
    return () => window.clearInterval(interval);
  }, [tickMs]);

  return now;
}

type TAnalysisInProgressScreenProps = {
  setup?: TSetupProgress | null;
  accent?: string;
  onCancel?: () => void;
  isCancelling?: boolean;
  onBack?: () => void;
};

export function AnalysisInProgressScreen({
  setup,
  accent,
  onCancel,
  isCancelling,
  onBack
}: TAnalysisInProgressScreenProps) {
  const { t } = useTranslation("projectHome");
  const { t: tCommon } = useTranslation("common");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const now = useNow(CLOCK_TICK_MS);
  const minutesLeft = setupMinutesLeft(setup ?? null, now);
  const isPastEta = minutesLeft <= 0;

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div data-testid="analysis-in-progress" className="flex flex-col items-center px-8 py-10 mt-[-40px]">
        <AnalysisFolder
          label={isPastEta ? t("generation.readySoonLabel") : t("generation.readyIn")}
          eta={isPastEta ? t("generation.readySoon") : formatMinutesLeft(t, minutesLeft)}
          accent={accent}
        />
        <FigmaTypography variant="heading-2" className="mt-[22px] text-center">
          {t("generation.analysisInProgressTitle")}
        </FigmaTypography>
        <FigmaTypography variant="body-2" color="zinc500" className="mt-1 max-w-[420px] text-center">
          {t("generation.analysisInProgressDescription")}
        </FigmaTypography>
        {onCancel && (
          <>
            <Button
              type="button"
              variant="outline"
              data-testid="analysis-in-progress-cancel-button"
              className="mt-4 shadow-none"
              disabled={isCancelling}
              onClick={() => setIsConfirmOpen(true)}
            >
              {isCancelling ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={tCommon("loading")} />
                  {tCommon("loading")}
                </>
              ) : (
                t("generation.cancelAnalysis")
              )}
            </Button>
            <CancelAnalysisModal isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} onConfirm={onCancel} />
          </>
        )}
        {!onCancel && onBack && (
          <Button
            type="button"
            variant="outline"
            data-testid="analysis-in-progress-back-button"
            className="mt-4 shadow-none"
            onClick={onBack}
          >
            {t("generation.backToProjectList")}
          </Button>
        )}
      </div>
    </div>
  );
}
